/**
 * PageContextProvider — tracks the current active window/tab so the AI
 * assistant can give context-aware advice.
 *
 * The active tab info is pushed via `setActivePage()` whenever TabManager
 * switches tabs.  The list of open windows is pulled from the
 * WindowCommandBus (which windows register with on mount).
 */
import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { windowCommandBus } from '../utils/windowCommandBus'

export interface ActivePageInfo {
  window_type: string
  tab_title?: string
  project_name?: string
}

interface PageContextValue {
  activePage: ActivePageInfo | null
  setActivePage: (info: ActivePageInfo | null) => void
  /** Build the full page context payload for the assistant API */
  buildPageContext: () => {
    window_type: string
    tab_title?: string
    project_name?: string
    open_windows?: Array<{ window_type: string; tab_id: string; label: string }>
  } | null
}

const PageContextCtx = createContext<PageContextValue | null>(null)

export const PageContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activePage, setActivePage] = useState<ActivePageInfo | null>(null)
  const [, forceUpdate] = useState({})

  // Re-render when windows register/unregister so buildPageContext is fresh
  useEffect(() => {
    const unsub = windowCommandBus.addListener(() => forceUpdate({}))
    return unsub
  }, [])

  const buildPageContext = useCallback(() => {
    if (!activePage) return null
    const openWindows = windowCommandBus.listWindows().map(w => ({
      window_type: w.windowType,
      tab_id: w.tabId,
      label: w.label,
    }))
    return {
      window_type: activePage.window_type,
      tab_title: activePage.tab_title,
      project_name: activePage.project_name,
      open_windows: openWindows.length > 0 ? openWindows : undefined,
    }
  }, [activePage])

  return (
    <PageContextCtx.Provider value={{ activePage, setActivePage, buildPageContext }}>
      {children}
    </PageContextCtx.Provider>
  )
}

export const usePageContext = (): PageContextValue => {
  const ctx = useContext(PageContextCtx)
  if (!ctx) {
    // Graceful fallback when used outside provider (e.g. farm marketplace)
    return {
      activePage: null,
      setActivePage: () => {},
      buildPageContext: () => null,
    }
  }
  return ctx
}